// Confirmation-email preview — RON-5466 Phase 2. Prototype: `EmailFrame`, `EmailMap`,
// `buildVisitorEmail`, `qrSvg`.
//
// A read-only preview of what the visitor will receive. The template itself is admin-managed
// (`tblvisitor_email_template`, §7) and cannot be edited here; merge fields are filled in
// automatically. Sending happens server-side through Ronspot's existing SES setup.
//
// TWO DELIBERATE SIMPLIFICATIONS vs the prototype, both flagged for the email work (RON-5470):
//
// 1. QR CODE — the prototype hand-draws a fake QR from a hash pattern. Rendering a
//    non-scannable barcode in a customer-facing preview invites "the QR is broken" reports, so
//    this shows a labelled placeholder with the booking reference instead. The real QR encodes
//    the visit `guid` and is generated by the backend for the actual email.
//
// 2. MAP — the prototype embeds a keyless Google Maps iframe. Email clients strip iframes, so
//    the real email needs a STATIC map image (Google Static Maps API + key). Rather than preview
//    something that cannot ship, this shows the site address and a "Get directions" link. The
//    static-image requirement is an API-contract item for RON-5470.

import { useTranslation } from "react-i18next";

import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import { VISIT_TYPE } from "../visitorConstants.js";

const directionsUrl = (site) =>
  site?.lat && site?.lng
    ? `https://www.google.com/maps/dir/?api=1&destination=${site.lat},${site.lng}`
    : null;

const VisitorEmailPreview = ({ form, site, building, visitors }) => {
  const { t } = useTranslation("common");

  const names = visitors
    .map((visitor) => visitor.name.trim())
    .filter(Boolean)
    .join(", ");

  const isMultiDay = form.startDate !== form.endDate;
  const mapUrl = directionsUrl(site);

  const rows = [
    [t("date"), formatDateToEEEddMMM(form.startDate) + (isMultiDay ? ` – ${formatDateToEEEddMMM(form.endDate)}` : "")],
    [t("react_visitors_field_arrival"), form.arrivalTime],
    [t("react_visitors_field_expected_out"), form.expectedOut],
    [t("react_visitors_field_site"), site?.name || "—"],
    building ? [t("react_visitors_field_building"), building.name] : null,
    [t("react_visitors_field_host"), form.host],
    form.type === VISIT_TYPE.VIP
      ? [t("react_visitors_col_type"), t("react_visitors_vip")]
      : null,
  ].filter(Boolean);

  return (
    <div className="visitor-email">
      <div className="visitor-email__meta">
        <span className="visitor-email__meta-label">{t("react_visitors_email_to")}</span>
        <span>{names || t("react_visitors_unnamed")}</span>
      </div>

      <p className="visitor-email__subject">
        {t("react_visitors_email_subject")
          .replace("%s", site?.name || "")
          .replace("%d", formatDateToEEEddMMM(form.startDate))}
      </p>

      <dl className="visitor-email__rows">
        {rows.map(([label, value]) => (
          <div className="visitor-email__row" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <div className="visitor-email__qr">
        <span className="visitor-email__qr-box" aria-hidden="true">
          QR
        </span>
        <span className="visitor-email__qr-note">
          {t("react_visitors_email_qr_note")}
        </span>
      </div>

      {site?.address && (
        <div className="visitor-email__map">
          <span className="visitor-email__map-title">
            {t("react_visitors_email_getting_here")}
          </span>
          <span className="visitor-email__map-address">{site.address}</span>
          {mapUrl && (
            <a
              className="visitor-email__map-link"
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t("react_visitors_email_directions")}
            </a>
          )}
        </div>
      )}

      <p className="visitor-email__footer">{t("react_visitors_email_managed")}</p>
    </div>
  );
};

export default VisitorEmailPreview;
